import { useCallback, useEffect, useMemo, useState } from "react";
import {
  collectZoneImages,
  groupZoneImages,
  type AssetSlot,
  type AssetSpec,
  type ZoneImageRef,
} from "@greg-chick/shared";
import {
  loadZoneMap,
  loadManifest,
  loadSlots,
  publishAssets,
  type Manifest,
  type ManifestEntry,
} from "../lib/contentApi";
import { SlotUploader } from "./SlotUploader";
import { useRegisterSave } from "../lib/saveBar";
import { Msg, Empty } from "./ui";

/**
 * 체험존 그림 교체(FR-ADMIN / FR-WORLD).
 *
 * 왜 따로 있나: 기존 '그림 넣기' 탭은 **올릴 수 있는 자리(슬롯)** 기준이라,
 * "경주존 그림 바꾸자" 할 때 그 존에 무엇이 쓰이는지 알 수 없었다(외우거나 맵을 봐야 했다).
 * 여기서는 **맵을 읽어** 실제로 쓰이는 그림만 모아 보여준다 — 새 오브젝트를 배치하면
 * 목록에 자동으로 생기고, 안 쓰는 그림은 보이지 않는다(콘텐츠 주도, 하드코딩 없음).
 */
const ZONES = [
  { id: "lobby", label: "마을(로비)" },
  { id: "gyeongju", label: "경주" },
  { id: "book", label: "전자책방" },
  { id: "goods", label: "굿즈 마켓" },
  { id: "event", label: "행사장" },
];

/** 슬롯키 → 규격. 접두사가 긴 것부터 맞춰본다(world.background 가 world 보다 먼저). */
function specFor(slotKey: string, specs: AssetSlot[]): AssetSpec {
  const hit = specs
    .filter((s) => slotKey === s.key || slotKey.startsWith(`${s.key}.`))
    .sort((a, b) => b.key.length - a.key.length)[0];
  return hit?.spec ?? { formats: ["webp", "png"] };
}

/** 그림이 없을 때 뭐라고 안내할지 — 존 바탕은 코드로 그려지고, 나머지는 그냥 비어 있다. */
function proceduralNote(group: ZoneImageRef["group"]): string | undefined {
  if (group === "bg") return "코드로 그린 기본 배경·바닥·벽이 보여요";
  if (group === "npc" || group === "decor") return "코드로 그린 기본 모양이 보여요";
  return undefined;
}

export function ZoneImagePanel() {
  const [zone, setZone] = useState("gyeongju");
  const [refs, setRefs] = useState<ZoneImageRef[]>([]);
  const [manifest, setManifest] = useState<Manifest>({ version: 0, assets: {} });
  const [specs, setSpecs] = useState<AssetSlot[]>([]);
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [msg, setMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSlots().then(setSpecs).catch(() => setSpecs([]));
    loadManifest().then(setManifest).catch(() => undefined);
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    loadZoneMap(zone)
      .then((map) => {
        if (!alive) return;
        setRefs(collectZoneImages(zone, map));
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setRefs(collectZoneImages(zone, null)); // 맵을 못 읽어도 바탕 3종은 바꿀 수 있게
        setMsg("맵을 못 읽어서 바탕 그림만 보여요.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [zone]);

  const sections = useMemo(() => groupZoneImages(refs), [refs]);

  // 내가 바꾼 키만 추적 → 게시 때 최신 게시본에 **내 변경분만** 얹는다(동시편집 덮어쓰기 방지).
  const onChange = useCallback((key: string, entry: ManifestEntry | null) => {
    setManifest((m) => {
      const assets = { ...m.assets };
      if (entry) assets[key] = entry;
      else delete assets[key];
      return { ...m, assets };
    });
    setDirty((d) => new Set(d).add(key));
  }, []);

  async function onPublish() {
    const latest = await loadManifest().catch(() => ({ version: 0, assets: {} }) as Manifest);
    const merged = { ...latest.assets };
    for (const k of dirty) {
      const e = manifest.assets[k];
      if (e) merged[k] = e;
      else delete merged[k];
    }
    const r = await publishAssets(merged);
    setDirty(new Set());
    setManifest({ version: r.version, assets: merged });
    setMsg(`🚀 게시했어요 (v${r.version}) — 웹에 바로 보여요.`);
  }

  // 그림 게시는 '그림 전체 목록'과 **같은 매니페스트**를 올린다 — 버튼이 두 곳에 있으면
  // "어느 걸 눌러야 하지"가 된다. 하단 고정 바 하나로 모은다.
  // 라벨은 화면마다 달라야 한다 — [그림 전체 목록]도 같은 매니페스트를 올리는데
  // 둘 다 "그림"이면 저장 바에 같은 이름 버튼이 둘 뜬다.
  useRegisterSave({
    id: "zoneimg",
    label: "체험존 그림",
    dirty: dirty.size,
    save: onPublish,
    // 💾 바꾼 그림 자리만 담는다 — 그림 파일 자체는 올릴 때 이미 서버에 있다.
    //    지운 자리는 null 로 남긴다(키를 빼면 "안 지운 것"이 된다).
    draft: {
      data: () => ({
        entries: Object.fromEntries([...dirty].map((k) => [k, manifest.assets[k] ?? null])),
        dirty: [...dirty],
      }),
      restore: (d) => {
        const v = d as { entries?: Record<string, ManifestEntry | null>; dirty?: string[] };
        if (!v?.entries) return;
        setManifest((m) => {
          const next = { ...m.assets };
          for (const [k, e] of Object.entries(v.entries!)) {
            if (e) next[k] = e;
            else delete next[k];
          }
          return { ...m, assets: next };
        });
        setDirty(new Set(v.dirty ?? Object.keys(v.entries!)));
      },
    },
  });

  const zoneLabel = ZONES.find((z) => z.id === zone)?.label ?? zone;

  return (
    <section className="panel zoneimg">
      <div className="zoneimg__bar">
        {ZONES.map((z) => (
          <button
            key={z.id}
            className={`btn btn--sm${z.id === zone ? " btn--primary" : ""}`}
            onClick={() => setZone(z.id)}
          >
            {z.label}
          </button>
        ))}
      </div>

      <Msg text={msg} />
      {loading ? <p className="hint">{zoneLabel} 그림을 찾는 중…</p> : null}

      {sections.map((sec) => (
        <div key={sec.group} className="zoneimg__group">
          <h3>
            {sec.label} <em>{sec.items.length}</em>
          </h3>
          <div className="grid">
            {sec.items.map((r) => (
              <SlotUploader
                key={r.slotKey}
                slotKey={r.slotKey}
                label={r.label}
                spec={specFor(r.slotKey, specs)}
                entry={manifest.assets[r.slotKey]}
                onChange={onChange}
                alt={r.label}
                procedural={manifest.assets[r.slotKey] ? undefined : proceduralNote(r.group)}
              />
            ))}
          </div>
        </div>
      ))}

      {!loading && sections.length <= 1 ? (
        <Empty icon="🏞️" action="친구·장식을 놓으면 여기에 그 그림 칸이 생겨요">
          이 마을엔 배경 말고 다른 그림이 아직 없어요. <b>🗺️ 체험공간 배치</b>에서 놓아보세요.
        </Empty>
      ) : null}
    </section>
  );
}
